import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from 'src/app/shared/snackbar/snackbar.service';
import { StockService } from '../../services/stock.service';
import { StorageService } from 'src/app/pages/storage/services/storage.service';
import { StoreStockAndLot, StoreStockLot } from '../../interfaces/stock.interface';
import { StoreStockLotsService } from '../../services/store-stock-lots.service';

interface StoreOption {
  id: number;
  name: string;
  address: string;
}

interface QuantityForm {
  quantity: FormControl<number>;
  sourceStoreId: FormControl<number | undefined>;
  destinationStoreId: FormControl<number | undefined>;
}

@Component({
  selector: 'app-stock-transfer-form',
  templateUrl: './stock-transfer-form.component.html',
  styleUrls: ['./stock-transfer-form.component.scss']
})
export class StockTransferFormComponent implements OnInit, OnDestroy {
  @Input() stockData!: StoreStockAndLot;
  @Output() formSubmitted = new EventEmitter<void>();
  @Output() stepBackEmitter = new EventEmitter<void>();

  stockAvailable: number = 0;

  private readonly destroy$ = new Subject<void>();

  transferForm: FormGroup<QuantityForm> | null = null;
  isLoading = false;
  isLoadingStores = false;
  availableStores: StoreOption[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly storeStockLotsService: StoreStockLotsService,
    private readonly storageService: StorageService,
    private readonly translate: TranslateService,
    private readonly snackbarService: SnackbarService
  ) {
  }

  ngOnInit(): void {
    console.log("data: ", this.stockData)
    this.loadStores();
    this.transferForm = this.createForm(this.stockData)
    console.log(this.transferForm)
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createForm(data: StoreStockAndLot): FormGroup<QuantityForm> {
    this.stockAvailable = data.lot.stockQuantity - (data.lot.reservedStock ?? 0);
    return this.fb.group({
      sourceStoreId: this.fb.nonNullable.control<number | undefined>(data.stock.storeId, Validators.required),
      destinationStoreId: this.fb.nonNullable.control<number | undefined>(undefined, Validators.required),
      quantity: this.fb.nonNullable.control<number>(this.stockAvailable ?? 1, [Validators.required, Validators.min(1), Validators.max(this.stockAvailable)]),
    });
  }

  private loadStores(): void {
    this.isLoadingStores = true;
    this.storageService.getAllStorages()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (stores) => {
          this.availableStores = stores
            .filter(store => store.id !== this.stockData.stock.storeId)
            .map(store => ({
              id: store.id,
              name: store.name,
              address: store.address || ''
            }));
          this.isLoadingStores = false;
        },
        error: (error) => {
          console.error('Error loading stores:', error);
          this.isLoadingStores = false;
          this.snackbarService.showError(
            this.translate.instant('pages.stock.edit.error_loading_stores')
          );
        }
      });
  }

  get quantity(): number {
    return this.transferForm?.controls.quantity.value || 0;
  }

  get selectedStore(): StoreOption | null {
    const storeId = this.transferForm?.controls.destinationStoreId?.value;
    if (!storeId) return null;
    return this.availableStores.find(store => store.id === storeId) || null;
  }

  get remainingQuantity(): number {
    return this.stockData.lot.stockQuantity - this.quantity;
  }

  getDestinationCurrentStock(): number {
    // TODO: Implement logic to get current stock in destination store
    // For now, we assume 0 as we don't have this yet
    return 0;
  }

  getDestinationFinalStock(): number {
    return this.getDestinationCurrentStock() + this.quantity;
  }

  onQuantityChange(): void {
    const quantity = this.quantity;
    if (quantity > this.stockAvailable) {
      this.transferForm?.controls.quantity.setValue(
        this.stockAvailable
      );
    }
  }

  increaseQuantity(): void {
    const currentValue = this.quantity;
    if (currentValue < this.stockAvailable) {
      this.transferForm?.controls.quantity.setValue(
        currentValue + 1
      );
    }
  }

  decreaseQuantity(): void {
    const currentValue = this.quantity;
    if (currentValue > 1) {
      this.transferForm?.controls.quantity.setValue(
        currentValue - 1
      );
    }
  }

  onSubmit(): void {
    if (this.transferForm?.valid && !this.isLoading) {
      const formValue = this.transferForm.value;

      this.isLoading = true;

      const destinationStoreId: number = Number(formValue.destinationStoreId!)
      const quantity: number = formValue.quantity!

      this.storeStockLotsService.transferLot(this.stockData.lot.lotId, destinationStoreId, quantity)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.isLoading = false;
            this.formSubmitted.emit();
          },
          error: (error) => {
            console.error('Error transferring stock:', error);
            this.isLoading = false;
          }
        });
    } else {
      this.transferForm?.markAllAsTouched();
    }
  }

  goBack(): void {
    this.stepBackEmitter.emit();
  }
} 